import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const Designations = () => {
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDesignationId, setEditingDesignationId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState('');

  const [formData, setFormData] = useState({
    name: "",
    department: ""
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchDepartments(selectedCompanyId);
      fetchDesignations();
    }
  }, [selectedCompanyId]);

  const fetchCompanies = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found. Please log in.");
      setLoading(false);
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      console.error("Invalid token:", err);
      setError("Invalid token. Please log in again.");
      setLoading(false);
      return;
    }

    // Extract companyId from token
    const companyId = decoded.companyId || decoded.id;
    if (!companyId) {
      setError("No companyId found in token.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/companies/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCompanies([response.data]);
      setSelectedCompanyId(companyId);
    } catch (err) {
      console.error("Error fetching companies:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      setError("Failed to fetch companies.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (companyId) => {
    if (!companyId) return;
    const token = localStorage.getItem("authToken");

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepartments(response.data);
    } catch (err) {
      console.error("Error fetching departments:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      setError("Failed to fetch departments.");
    }
  };

  const fetchDesignations = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found. Please log in.");
      setLoading(false);
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      console.error("Invalid token:", err);
      localStorage.removeItem("authToken");
      setError("Invalid token. Please log in again.");
      window.location.href = '/login';
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/designations/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log("Designations API response:", response.data);
      
      // Handle different response structures
      let designationsData = [];
      if (Array.isArray(response.data)) {
        designationsData = response.data;
      } else if (response.data && Array.isArray(response.data.designations)) {
        designationsData = response.data.designations;
      } else if (response.data && Array.isArray(response.data.data)) {
        designationsData = response.data.data;
      }
      
      setDesignations(designationsData);
      setError(null);
    } catch (err) {
      console.error("Error fetching designations:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      setDesignations([]);
      setError(err.response?.data?.message || "Failed to fetch designations.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleAddDesignation = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!formData.name.trim()) {
      alert("Please enter a designation name.");
      return;
    }

    if (!formData.department) {
      alert("Please select a department.");
      return;
    }

    try {
      const response = await axios.post(
        `https://customersupport-jhy9.onrender.com/api/designations/`,
        {
          name: formData.name.trim(),
          department: formData.department
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Designation created:", response.data);
      fetchDesignations();
      resetForm();
      alert("Designation created successfully!");
    } catch (err) {
      console.error("Error creating designation:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      alert(err.response?.data?.message || "Error creating designation");
    }
  };

  const handleEditDesignation = (designation) => {
    setIsEditing(true);
    setEditingDesignationId(designation._id);
    setFormData({
      name: designation.name || "",
      department: designation.department?._id || designation.department || ""
    });
    setShowForm(true);
  };

  const handleUpdateDesignation = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!formData.name.trim()) {
      alert("Please enter a designation name.");
      return;
    }

    if (!formData.department) {
      alert("Please select a department.");
      return;
    }

    try {
      const response = await axios.put(
        `https://customersupport-jhy9.onrender.com/api/designations/${editingDesignationId}`,
        {
          name: formData.name.trim(),
          department: formData.department
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Designation updated:", response.data);
      fetchDesignations();
      resetForm();
      alert("Designation updated successfully!");
    } catch (err) {
      console.error("Error updating designation:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      alert(err.response?.data?.message || "Error updating designation");
    }
  };

  const handleDeleteDesignation = async (id) => {
    if (!window.confirm("Are you sure you want to delete this designation?")) return;
    const token = localStorage.getItem("authToken");

    try {
      await axios.delete(`https://customersupport-jhy9.onrender.com/api/designations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      fetchDesignations();
      alert("Designation deleted successfully!");
    } catch (err) {
      console.error("Error deleting designation:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      alert(err.response?.data?.message || "Error deleting designation");
    }
  };

  const handleDepartmentFilterChange = (e) => {
    setSelectedDepartmentFilter(e.target.value);
  };

  const filteredDesignations = selectedDepartmentFilter 
    ? designations.filter(designation => 
        designation.department?._id === selectedDepartmentFilter || 
        designation.department === selectedDepartmentFilter
      )
    : designations;

  const resetForm = () => {
    setFormData({
      name: "",
      department: ""
    });
    setIsEditing(false);
    setEditingDesignationId(null);
    setShowForm(false);
  };

  if (loading && (!Array.isArray(designations) || !designations.length)) return <p className="text-center">Loading...</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 space-y-4 lg:space-y-0">
          <h1 className="text-3xl font-bold text-gray-800">Designation Management</h1>
          
          <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
            {/* Department Filter */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Filter by Department</label>
              <select
                value={selectedDepartmentFilter}
                onChange={handleDepartmentFilterChange}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white min-w-[200px]"
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Company Display */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Company</label>
              <div className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm">
                {companies[0]?.businessName || 'Loading company...'}
              </div>
            </div>
            
            <div className="flex flex-col justify-end">
              <button
                onClick={() => (showForm ? resetForm() : setShowForm(true))}
                className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
              >
                {showForm ? "Cancel" : "Add Designation"}
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-6">
              {isEditing ? "Edit Designation" : "Add New Designation"}
            </h2>
            <form onSubmit={isEditing ? handleUpdateDesignation : handleAddDesignation}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Designation Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Manager, Developer, HR"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department *
                  </label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
                >
                  {isEditing ? "Update Designation" : "Add Designation"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Designations List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">Designations</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {!Array.isArray(filteredDesignations) || filteredDesignations.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {selectedDepartmentFilter 
                  ? `No designations found for the selected department.` 
                  : `No designations found. Add a designation to get started.`
                }
              </div>
            ) : (
              filteredDesignations.map((designation) => (
                <div key={designation._id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{designation.name}</h3>
                      <p className="text-sm text-gray-500">
                        Department: {designation.department?.name || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        ID: {designation._id}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditDesignation(designation)}
                        className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded transition-colors duration-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDesignation(designation._id)}
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Designations;
